import { cn } from "@/lib/utils/general";
import { useEffect, useRef, useState, type CSSProperties } from "react";

interface ProgressiveImageProps {
  src: string;
  alt: string;
  className?: string;
  thumbnailSrc?: string;
  aspectRatio?: "video" | "square" | "portrait" | "auto";
  onLoad?: () => void;
  priority?: boolean;
  onClick?: () => void;
  /** Custom styles applied to the image element (e.g., objectPosition) */
  style?: CSSProperties;
  /** How the image should fit within its container. Default is "cover" */
  objectFit?: "cover" | "contain";
}

/** Check if an image is already cached by the browser */
function isImageCached(url: string): boolean {
  const img = new Image();
  img.src = url;
  return img.complete && img.naturalWidth > 0;
}

export function ProgressiveImage({
  src,
  alt,
  className,
  thumbnailSrc,
  aspectRatio = "auto",
  onLoad,
  priority = false,
  onClick,
  style,
  objectFit = "cover",
}: ProgressiveImageProps) {
  // If the full image is already cached, skip the blur transition entirely
  const cached = useRef(isImageCached(src));
  const [imgSrc, setImgSrc] = useState(cached.current ? src : (thumbnailSrc || src));
  const [isLoading, setIsLoading] = useState(!cached.current);
  const [isInView, setIsInView] = useState(priority || cached.current);
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevSrcRef = useRef(src);

  // Reset state when src changes (e.g., navigating a photo gallery)
  if (prevSrcRef.current !== src) {
    prevSrcRef.current = src;
    const nowCached = isImageCached(src);
    cached.current = nowCached;
    setImgSrc(nowCached ? src : (thumbnailSrc || src));
    setIsLoading(!nowCached);
    setIsInView(priority || nowCached);
  }

  // Lazy loading with Intersection Observer
  useEffect(() => {
    if (priority || cached.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: "50px",
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  // Load high-quality image when in view
  useEffect(() => {
    if (!isInView || cached.current) return;
    if (!thumbnailSrc || imgSrc === src) return;

    const img = new Image();
    img.src = src;
    img.onload = () => {
      setImgSrc(src);
      setIsLoading(false);
      onLoad?.();
    };
  }, [isInView, src, thumbnailSrc, imgSrc, onLoad]);

  // Handle direct loading without thumbnail
  useEffect(() => {
    if (cached.current) return;
    if (!thumbnailSrc && isInView) {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        setIsLoading(false);
        onLoad?.();
      };
    }
  }, [src, thumbnailSrc, isInView, onLoad]);

  const aspectRatioClasses = {
    video: "aspect-video",
    square: "aspect-square",
    portrait: "aspect-[4/5]",
    auto: "",
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative overflow-hidden bg-muted group",
        aspectRatioClasses[aspectRatio],
        onClick && "cursor-pointer",
        className
      )}
      onClick={onClick}
      style={{ touchAction: 'manipulation' }}
    >
      {isInView && (
        <img
          ref={imgRef}
          src={imgSrc}
          alt={alt}
          className={cn(
            "w-full h-full transition-[filter,transform] duration-300",
            objectFit === "cover" ? "object-cover" : "object-contain",
            isLoading && thumbnailSrc ? "blur-lg scale-105" : "blur-0 scale-100",
            onClick && "group-hover:scale-105"
          )}
          style={style}
          loading={priority ? "eager" : "lazy"}
          draggable={false}
        />
      )}
      {!isInView && (
        <div className="absolute inset-0 bg-muted animate-pulse" />
      )}
    </div>
  );
}